/**
 * Progress helpers shared by the Tracker, Awards and Leader Board screens:
 * chart period ranges, BMI maths and the points → award level thresholds.
 */
import { LBS_PER_KG, normalizeWeightUnit } from './weightUtils';

export type ChartPeriod = 'daily' | 'weekly' | 'monthly';
export type ChartName = 'weight' | 'bmi';

export interface ChartDateRange {
  from: string;
  to: string;
}

/** The `periodicalType` value GetChartData expects for each period tab. */
export const mapPeriodicalType = (period: ChartPeriod): string => {
  switch (period) {
    case 'daily':
      return 'Daily';
    case 'weekly':
      return 'Weekly';
    default:
      return 'Monthly';
  }
};

/** Range covered by a period tab: today, the current week, or the current month. */
export const getChartDateRange = (
  period: ChartPeriod,
  now: Date = new Date(),
): ChartDateRange => {
  const year = now.getFullYear();
  const month = now.getMonth();
  const date = now.getDate();

  if (period === 'daily') {
    return {
      from: new Date(year, month, date, 0, 0, 0, 0).toISOString(),
      to: new Date(year, month, date, 23, 59, 59, 999).toISOString(),
    };
  }

  if (period === 'weekly') {
    const weekStart = new Date(year, month, date - now.getDay(), 0, 0, 0, 0);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);
    return { from: weekStart.toISOString(), to: weekEnd.toISOString() };
  }

  return {
    from: new Date(year, month, 1, 0, 0, 0, 0).toISOString(),
    to: new Date(year, month + 1, 0, 23, 59, 59, 999).toISOString(),
  };
};

export type BmiRangeKey = 'underweight' | 'normal' | 'overweight' | 'obese';

export interface BmiRange {
  key: BmiRangeKey;
  /** Position of the marker on the 4-segment range bar, 0-100. */
  percent: number;
}

/** BMI from a weight in the user's unit plus a height in inches. */
export const calculateBmi = (
  weight?: number | null,
  heightInches?: number | null,
  unit?: string | null,
): number | null => {
  const weightValue = Number(weight);
  const height = Number(heightInches);
  if (!Number.isFinite(weightValue) || weightValue <= 0) return null;
  if (!Number.isFinite(height) || height <= 0) return null;

  const weightLbs =
    normalizeWeightUnit(unit) === 'kg' ? weightValue * LBS_PER_KG : weightValue;
  const bmi = (weightLbs * 703) / (height * height);
  return Number.isFinite(bmi) ? Number(bmi.toFixed(1)) : null;
};

/** Which of the four BMI bands a value sits in, and where on the bar it lands. */
export const getBmiRange = (bmi: number | null): BmiRange => {
  if (bmi == null || !Number.isFinite(bmi)) {
    return { key: 'normal', percent: 50 };
  }
  if (bmi < 18.5) {
    return { key: 'underweight', percent: Math.max(0, (bmi / 18.5) * 25) };
  }
  if (bmi < 25) {
    return { key: 'normal', percent: 25 + ((bmi - 18.5) / 6.5) * 25 };
  }
  if (bmi < 30) {
    return { key: 'overweight', percent: 50 + ((bmi - 25) / 5) * 25 };
  }
  return { key: 'obese', percent: 75 + (Math.min(10, bmi - 30) / 10) * 25 };
};

export type AwardLevelKey =
  | 'starter'
  | 'risingStar'
  | 'supporter'
  | 'energizer'
  | 'achiever'
  | 'champion'
  | 'legend'
  | 'elite';

/** Points thresholds behind the rank badges (leader board and awards agree). */
export const getAwardLevelKey = (totalPoints?: number | null): AwardLevelKey => {
  const points = Number(totalPoints) || 0;
  if (points >= 20000) return 'elite';
  if (points >= 10000) return 'legend';
  if (points >= 1000) return 'champion';
  if (points >= 700) return 'achiever';
  if (points >= 400) return 'energizer';
  if (points >= 200) return 'supporter';
  if (points >= 100) return 'risingStar';
  return 'starter';
};

/** Maps the API's `iconKey` for an award level onto the level keys above. */
export const awardIconKeyToLevel = (iconKey?: string | null): AwardLevelKey => {
  switch (String(iconKey ?? '').toLowerCase()) {
    case 'star':
      return 'risingStar';
    case 'heart':
      return 'supporter';
    case 'lightning':
      return 'energizer';
    case 'trophy':
      return 'achiever';
    case 'medal':
      return 'champion';
    case 'diamond':
      return 'legend';
    case 'crown':
      return 'elite';
    case 'sprout':
    default:
      return 'starter';
  }
};
